import type {TripTemplate} from "@/types/tripTemplates";
import {normalizeTemplateTopicParam} from "@/navigation/templateDeepLink";

const EVENT_TOPIC_STARTS: Record<string, {year: number; month: number; day: number}> = {
  "world-cup-2026": {year: 2026, month: 6, day: 11},
  "calgary-stampede": {year: 2026, month: 7, day: 3},
  "america-250": {year: 2026, month: 7, day: 3},
};

const MONTH_INDEX: Record<string, number> = {
  jan: 0,
  january: 0,
  feb: 1,
  february: 1,
  mar: 2,
  march: 2,
  apr: 3,
  april: 3,
  may: 4,
  jun: 5,
  june: 5,
  jul: 6,
  july: 6,
  aug: 7,
  august: 7,
  sep: 8,
  sept: 8,
  september: 8,
  oct: 9,
  october: 9,
  nov: 10,
  november: 10,
  dec: 11,
  december: 11,
};

function noonLocalDate(year: number, monthIndex: number, day: number): Date {
  const date = new Date(year, monthIndex, day);
  date.setHours(12, 0, 0, 0);
  return date;
}

function todayAtNoon(): Date {
  const date = new Date();
  date.setHours(12, 0, 0, 0);
  return date;
}

function parseMonthIndex(label: string): number | null {
  const key = label.trim().toLowerCase();
  return MONTH_INDEX[key] ?? null;
}

function resolveTopicTag(template: TripTemplate, topicTag?: string): string | undefined {
  const normalized = normalizeTemplateTopicParam(topicTag);
  if (normalized) {
    return normalized;
  }
  for (const tag of template.topicTags ?? template.tags ?? []) {
    const fromTag = normalizeTemplateTopicParam(tag);
    if (fromTag) {
      return fromTag;
    }
  }
  return undefined;
}

function inferFromTopicAnchor(topicTag?: string, now: Date = todayAtNoon()): Date | null {
  if (!topicTag) {
    return null;
  }
  const anchor = EVENT_TOPIC_STARTS[topicTag];
  if (!anchor) {
    return null;
  }
  const candidate = noonLocalDate(anchor.year, anchor.month - 1, anchor.day);
  return candidate >= now ? candidate : null;
}

function inferFromBestMonths(bestMonths: string[], now: Date = todayAtNoon()): Date | null {
  if (!bestMonths.length) {
    return null;
  }
  const startYear = now.getFullYear();
  for (let yearOffset = 0; yearOffset <= 2; yearOffset += 1) {
    for (const monthLabel of bestMonths) {
      const monthIndex = parseMonthIndex(monthLabel);
      if (monthIndex == null) {
        continue;
      }
      const candidate = noonLocalDate(startYear + yearOffset, monthIndex, 15);
      if (candidate >= now) {
        return candidate;
      }
    }
  }
  return null;
}

export function inferDefaultTemplateStartDate(
  template: TripTemplate,
  topicTag?: string,
  now: Date = todayAtNoon(),
): Date {
  const resolvedTopic = resolveTopicTag(template, topicTag);
  return (
    inferFromTopicAnchor(resolvedTopic, now) ??
    inferFromBestMonths(template.bestMonths ?? [], now) ??
    noonLocalDate(now.getFullYear(), now.getMonth(), now.getDate() + 30)
  );
}
